import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DeleteOutlined as DeleteIcon } from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, FormControl, IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { UserAPI } from '../apis/UserAPI';
import { constants } from '../constants';
const isWorkspaceOnlyRole = (role) => {
    // No application_id at all
    if (!role.application_id)
        return true;
    // Handle string type
    if (typeof role.application_id === 'string') {
        const trimmed = role.application_id.trim().toLowerCase();
        return (trimmed === '' ||
            trimmed === '0' ||
            trimmed === 'null' ||
            trimmed === 'undefined');
    }
    // Handle array type
    if (Array.isArray(role.application_id)) {
        // Empty array
        if (role.application_id.length === 0)
            return true;
        // Array with only null, undefined, empty strings, "0", or "null" strings
        return role.application_id.every((id) => {
            if (id === null || id === undefined)
                return true;
            if (typeof id === 'string') {
                const trimmed = id.trim().toLowerCase();
                return (trimmed === '' ||
                    trimmed === '0' ||
                    trimmed === 'null' ||
                    trimmed === 'undefined');
            }
            return false;
        });
    }
    return false;
};
const UserDetailsModal = ({ open, onClose, user, onDelete, onWorkspaceRoleChange, onApplicationRoleChange }) => {
    const [applications, setApplications] = useState([]);
    const [roles, setRoles] = useState([]);
    const [applicationAccess, setApplicationAccess] = useState([]);
    const [workspaceRoles, setWorkspaceRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [updateLoading, setUpdateLoading] = useState({});
    // Load applications and roles when modal opens
    useEffect(() => {
        if (open && user) {
            loadApplicationsAndRoles();
            // Reset workspace roles when modal opens to ensure fresh data
            setWorkspaceRoles([]);
        }
        else if (!open) {
            // Reset state when modal closes
            setWorkspaceRoles([]);
            setApplicationAccess([]);
            setError(null);
        }
    }, [open, user]);
    // Set initial workspace roles when user changes
    useEffect(() => {
        if (user && roles.length > 0) {
            const userRoles = Array.isArray(user.role) ? user.role : [user.role];
            const workspaceOnlyRoles = roles.filter(isWorkspaceOnlyRole);
            // Find all user roles that are workspace-only
            const userWorkspaceRoles = userRoles.filter((roleName) => workspaceOnlyRoles.some((role) => role.role_name === roleName));
            setWorkspaceRoles(userWorkspaceRoles);
        }
    }, [user, roles]);
    const loadApplicationsAndRoles = async () => {
        setLoading(true);
        try {
            const [appsResponse, rolesResponse] = await Promise.all([
                UserAPI.getAllApplications(),
                UserAPI.getRoles()
            ]);
            setApplications(appsResponse.resource);
            setRoles(rolesResponse.resource);
            // Helper to check if role belongs to an application
            const roleMatchesApp = (role, appId) => {
                const appIds = Array.isArray(role.application_id)
                    ? role.application_id
                    : [role.application_id];
                return appIds.some((id) => String(id) === String(appId));
            };
            // Helper to get roles for an application
            const getRolesForApp = (appId) => rolesResponse.resource
                .filter((role) => roleMatchesApp(role, appId))
                .map((role) => role.role_name);
            // Build application access data using user's actual roles
            if (user) {
                const userRoles = Array.isArray(user.role)
                    ? user.role
                    : [user.role];
                const applicationAccess = appsResponse.resource.map((app) => {
                    const appRoles = getRolesForApp(app.id);
                    // Find which of the user's roles belong to this application
                    const userAppRoles = userRoles.filter((userRole) => appRoles.includes(userRole));
                    return {
                        applicationName: app.name,
                        accessLevel: userAppRoles, // User's actual roles for this app
                        availableRoles: appRoles // All available roles for this app
                    };
                });
                setApplicationAccess(applicationAccess);
            }
        }
        catch (error) {
            // Failed to load applications and roles - silently handle
            setError(`${constants.user_details_load_failed} ${error}`);
        }
        finally {
            setLoading(false);
        }
    };
    const handleWorkspaceRoleChange = (event) => {
        const newRoles = event.target.value;
        setWorkspaceRoles(newRoles);
        if (user && onWorkspaceRoleChange) {
            onWorkspaceRoleChange(user.id, newRoles);
        }
    };
    const processRoleChanges = async (app, rolesToAdd, rolesToRemove) => {
        for (const roleName of rolesToAdd) {
            const roleObj = roles.find((r) => {
                const appIds = Array.isArray(r.application_id)
                    ? r.application_id
                    : [r.application_id];
                return (r.role_name === roleName &&
                    appIds.some((id) => String(id) === String(app.id)));
            });
            if (roleObj && user) {
                await UserAPI.assignRole(user.email, {
                    roleId: Number.parseInt(roleObj.role_id)
                });
            }
        }
        for (const roleName of rolesToRemove) {
            const roleObj = roles.find((r) => {
                const appIds = Array.isArray(r.application_id)
                    ? r.application_id
                    : [r.application_id];
                return (r.role_name === roleName &&
                    appIds.some((id) => String(id) === String(app.id)));
            });
            if (roleObj && user) {
                await UserAPI.removeRole(user.id, Number.parseInt(roleObj.role_id));
            }
        }
    };
    const buildUpdatedRolesList = (applicationName, newAccess) => {
        const allApplicationRoles = [];
        for (const app of applicationAccess) {
            if (app.applicationName === applicationName) {
                allApplicationRoles.push(...newAccess);
            }
            else {
                allApplicationRoles.push(...app.accessLevel);
            }
        }
        return workspaceRoles.length > 0
            ? [...workspaceRoles, ...allApplicationRoles]
            : allApplicationRoles;
    };
    const handleApplicationAccessChange = async (applicationName, newAccess) => {
        if (!user)
            return;
        setError(null);
        setUpdateLoading((prev) => ({ ...prev, [applicationName]: true }));
        try {
            const app = applications.find((app) => app.name === applicationName);
            if (!app) {
                throw new Error(`Application ${applicationName} not found`);
            }
            const currentApp = applicationAccess.find((a) => a.applicationName === applicationName);
            const currentAccess = currentApp?.accessLevel || [];
            const rolesToAdd = newAccess.filter((role) => !currentAccess.includes(role));
            const rolesToRemove = currentAccess.filter((role) => !newAccess.includes(role));
            await processRoleChanges(app, rolesToAdd, rolesToRemove);
            setApplicationAccess((prev) => prev.map((app) => app.applicationName === applicationName
                ? { ...app, accessLevel: newAccess }
                : app));
            if (onApplicationRoleChange && user) {
                const allRoles = buildUpdatedRolesList(applicationName, newAccess);
                onApplicationRoleChange(user.id, allRoles);
            }
        }
        catch (error) {
            setError(error.message || constants.user_details_update_access_failed);
        }
        finally {
            setUpdateLoading((prev) => ({ ...prev, [applicationName]: false }));
        }
    };
    const handleDelete = () => {
        if (user && onDelete) {
            onDelete(user);
        }
        onClose();
    };
    // Helper function to get workspace-only roles (not assigned to any application)
    const getWorkspaceOnlyRoles = () => {
        return roles.filter(isWorkspaceOnlyRole);
    };
    if (!user)
        return null;
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "sm", fullWidth: true, PaperProps: {
            sx: {
                borderRadius: 2,
                maxHeight: '90vh'
            }
        }, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 3,
                    borderBottom: '1px solid #ebebeb'
                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 3 }, children: [_jsx(Typography, { variant: "h5", sx: {
                                    fontFamily: 'Open Sans',
                                    fontWeight: 600,
                                    fontSize: '24px',
                                    color: '#212121',
                                    lineHeight: 1.52
                                }, children: user.name }), _jsx(Typography, { variant: "body2", sx: {
                                    color: '#595959',
                                    fontSize: '14px'
                                }, children: constants.user_details_date_added(user.dateAdded) })] }), _jsx(IconButton, { onClick: handleDelete, sx: {
                            color: '#da291c',
                            border: '1px solid #da291c',
                            borderRadius: '2px',
                            width: 40,
                            height: 40,
                            '&:hover': {
                                backgroundColor: 'rgba(218, 41, 28, 0.04)'
                            }
                        }, children: _jsx(DeleteIcon, { fontSize: "small" }) })] }), _jsxs(DialogContent, { sx: { p: 3 }, children: [error && (_jsx(Alert, { severity: "error", sx: { mb: 3 }, onClose: () => setError(null), children: error })), _jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 3,
                            p: 2,
                            backgroundColor: '#f5f5f5',
                            borderRadius: 2,
                            border: '1px solid #ebebeb'
                        }, children: [_jsx(Typography, { sx: {
                                    fontFamily: 'Open Sans',
                                    fontSize: '14px',
                                    color: '#212121',
                                    minWidth: '120px'
                                }, children: constants.user_details_workspace_role }), _jsx(FormControl, { sx: { minWidth: 200 }, children: _jsx(Select, { multiple: true, value: workspaceRoles, onChange: handleWorkspaceRoleChange, size: "small", renderValue: (selected) => (_jsx(Box, { sx: {
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 0.5
                                        }, children: selected.length === 0 ? (_jsx(Typography, { variant: "body2", sx: { color: '#999' }, children: constants.user_details_no_roles_selected })) : (selected.map((value) => (_jsx(Chip, { label: value, size: "small", sx: {
                                                backgroundColor: '#f3f8eb',
                                                color: '#1a5d09',
                                                fontFamily: 'Open Sans',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                height: 20
                                            } }, value)))) })), sx: {
                                        backgroundColor: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d9d9d9'
                                        }
                                    }, children: getWorkspaceOnlyRoles().map((role) => (_jsx(MenuItem, { value: role.role_name, children: role.role_name }, role.role_id))) }) })] }), _jsx(Box, { children: _jsx(TableContainer, { component: Paper, elevation: 0, children: _jsxs(Table, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                        fontFamily: 'Open Sans',
                                                        fontWeight: 600,
                                                        fontSize: '14px',
                                                        color: '#212121',
                                                        borderBottom: '1px solid #ebebeb',
                                                        width: '300px'
                                                    }, children: constants.user_details_applications_header_application }), _jsx(TableCell, { sx: {
                                                        fontFamily: 'Open Sans',
                                                        fontWeight: 600,
                                                        fontSize: '14px',
                                                        color: '#212121',
                                                        borderBottom: '1px solid #ebebeb'
                                                    }, children: constants.user_details_applications_header_access })] }) }), _jsx(TableBody, { children: applicationAccess.map((app, index) => (_jsxs(TableRow, { sx: {
                                                '&:not(:last-child)': {
                                                    borderBottom: '1px solid #ebebeb'
                                                }
                                            }, children: [_jsx(TableCell, { sx: {
                                                        fontFamily: 'Open Sans',
                                                        fontSize: '14px',
                                                        color: '#212121',
                                                        borderBottom: index ===
                                                            applicationAccess.length - 1
                                                            ? 'none'
                                                            : '1px solid #ebebeb'
                                                    }, children: app.applicationName }), _jsx(TableCell, { sx: {
                                                        borderBottom: index ===
                                                            applicationAccess.length - 1
                                                            ? 'none'
                                                            : '1px solid #ebebeb'
                                                    }, children: _jsx(FormControl, { sx: { minWidth: 200 }, children: _jsx(Select, { multiple: true, value: app.accessLevel, onChange: (e) => handleApplicationAccessChange(app.applicationName, e.target
                                                                .value), size: "small", disabled: updateLoading[app.applicationName] || loading, renderValue: (selected) => (_jsxs(Box, { sx: {
                                                                    display: 'flex',
                                                                    flexWrap: 'wrap',
                                                                    gap: 0.5,
                                                                    alignItems: 'center'
                                                                }, children: [updateLoading[app
                                                                        .applicationName] && (_jsx(CircularProgress, { size: 16, sx: {
                                                                            mr: 1
                                                                        } })), selected.length === 0 ? (_jsx(Typography, { variant: "body2", sx: {
                                                                            color: '#999'
                                                                        }, children: constants.user_details_no_roles_selected })) : (selected.map((value) => (_jsx(Chip, { label: value, size: "small", sx: {
                                                                            backgroundColor: '#f3f8eb',
                                                                            color: '#1a5d09',
                                                                            fontFamily: 'Open Sans',
                                                                            fontSize: 12,
                                                                            fontWeight: 600,
                                                                            height: 20
                                                                        } }, value))))] })), sx: {
                                                                backgroundColor: 'white',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#d9d9d9'
                                                                }
                                                            }, children: app.availableRoles.map((role) => (_jsx(MenuItem, { value: role, children: role }, role))) }) }) })] }, app.applicationName))) })] }) }) })] }), _jsx(DialogActions, { sx: {
                    p: 3,
                    borderTop: '1px solid #d9d9d9',
                    justifyContent: 'flex-end'
                }, children: _jsx(Button, { onClick: onClose, variant: "contained", sx: {
                        backgroundColor: '#26890d',
                        color: 'white',
                        textTransform: 'none',
                        fontFamily: 'Open Sans',
                        fontWeight: 600,
                        fontSize: '16px',
                        px: 4,
                        py: 1.5,
                        '&:hover': {
                            backgroundColor: '#1a5d09'
                        }
                    }, children: constants.close }) })] }));
};
export default UserDetailsModal;
